import cookie from 'cookie';
import forIn from 'lodash/forIn';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import {parseJsonOrReturnString} from 'utils/string-utils';

import type {CookieSerializeOptions} from 'cookie';

const _cookies = new WeakMap<MeteorCookies, {[p: string]: string}>();
const COOKIE_OPTIONS = ['domain', 'encode', 'expires', 'httpOnly', 'maxAge', 'path', 'sameSite', 'secure'];

let instance: MeteorCookies = null;

export default class MeteorCookies {
    private readonly hasDocumentCookie: boolean;
    private readonly _options: CookieSerializeOptions;
    private storedCookies: string[];

    constructor(options?: CookieSerializeOptions) {
        if (isNil(instance)) {
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            instance = this;
        }
        this.hasDocumentCookie = MeteorCookies.hasDocumentCookie();
        this._options = {};
        this._refreshCookies();
        if (!isNil(options)) {
            this.setDefaultOptions(options);
        }

        return instance;
    }

    static hasDocumentCookie() {
        let result = false;
        if (isObject(document) && isString(document.cookie)) {
            result = true;
        }
        return result;
    }

    // here is a lovely hack!
    // we created no way to clear just cookies associated with the logged in user's session
    // which means any other cookies that we want to persist after logout will also be wiped
    // so here we explicitly enumerate the ones we want to keep
    static wipeDocumentCookies() {
        if (MeteorCookies.hasDocumentCookie()) {
            const cookies = document.cookie.split(';');
            cookies.forEach((c) => {
                if (
                    !c.includes('examInterfaceToken=') && // don't clear examInterfaceToken
                    !c.includes('centerSignInKioskToken=') &&
                    !c.includes('clientId')
                ) {
                    // don't clear centerSignInKioskToken
                    document.cookie = c
                        .replace(/^ +/, '')
                        .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
                }
            });
        }
    }

    // because of the hack above, we can't voluntarily keep cookies in several places they get removed
    // so here we are... storing them so we can restore them later
    storeCookies() {
        if (MeteorCookies.hasDocumentCookie()) {
            this.storedCookies = document.cookie.split(';');
        }
    }

    restoreCookies() {
        if (!isNil(this.storedCookies)) {
            MeteorCookies.wipeDocumentCookies();
            this.storedCookies.forEach((storedCookie) => {
                storedCookie = `${storedCookie};path=/`;
                document.cookie = storedCookie;
            });
        }
    }

    static getInstance() {
        return instance;
    }

    _refreshCookies() {
        if (this.hasDocumentCookie) {
            _cookies.set(instance, cookie.parse(document.cookie));
        }
    }

    setDefaultOptions(options: CookieSerializeOptions) {
        forIn(options, (value, key) => {
            if (COOKIE_OPTIONS.indexOf(key) !== -1) {
                this._options[key] = value;
            }
        });
    }

    get cookies() {
        this._refreshCookies();
        const result = {};
        forIn(_cookies.get(instance), (cookieValue, cookieKey) => {
            result[cookieKey] = this.get(cookieKey);
        });
        return result;
    }

    get(key: string, nilResult = undefined) {
        // TODO: add getOptions (response) to return options
        this._refreshCookies();
        let result = nilResult;
        const cookies = _cookies.get(instance);
        if (!isNil(cookies[key])) {
            result = parseJsonOrReturnString(cookies[key]);
        }
        return result;
    }

    set(key: string, value: string | object, options: CookieSerializeOptions = {}) {
        if (isObject(value)) {
            value = JSON.stringify(value);
        }
        options = {
            ...this._options,
            ...options,
        };
        if (this.hasDocumentCookie) {
            document.cookie = cookie.serialize(key, value, options);
        }
        this._refreshCookies();

        return instance;
    }

    remove(key: string, options: CookieSerializeOptions = {}) {
        options = {
            maxAge: 0,
            ...this._options,
            ...options,
        };

        const cookies = _cookies.get(instance);
        delete cookies[key];

        if (this.hasDocumentCookie) {
            document.cookie = cookie.serialize(key, '', options);
        }

        return instance;
    }
}
